import { memo, useCallback, useRef, useState } from "react";
import InlinePicker from "./InlinePicker";

/**
 * A multi-value cell: chips for what is set, a `+` that opens an inline picker
 * anchored under the cell. Units, tags and required evidence all use this —
 * they differ only in vocabulary and chip styling.
 *
 * Whether the picker is open is view state and stays here. Which values are
 * allowed, and whether a value may repeat, is decided by the caller.
 *
 * @param {object} props
 * @param {string[]} props.values
 * @param {string[]} props.catalogue everything pickable
 * @param {boolean} [props.dirty]
 * @param {boolean} [props.disabled]
 * @param {"tag"|"unit"|"plain"} [props.variant] chip styling only
 * @param {string} props.label used for the picker heading and aria labels
 * @param {boolean} [props.creatable] a value outside the catalogue may be typed in
 * @param {(value: string) => void} props.onToggle add if absent, remove if present
 */
function ChipsCell({ values, catalogue, dirty, disabled, variant = "plain", label, creatable, onToggle }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const cellRef = useRef(null);

  const pick = useCallback(
    (value) => {
      onToggle(value);
      setOpen(false);
    },
    [onToggle],
  );

  return (
    <div className={`kgb-chipcell${dirty ? " kgb-chipcell--dirty" : ""}`} ref={cellRef}>
      {values.map((value) => {
        const [head, ...rest] = value.split(":");
        const tail = rest.join(":");
        return (
          <span key={value} className={`kgb-chip kgb-chip--${variant}`}>
            {tail ? <span className="kgb-chip-key">{head}</span> : null}
            <span>{tail || head}</span>
            {disabled ? null : (
              <button
                type="button"
                className="kgb-chip-remove"
                aria-label={`Remove ${value} from ${label}`}
                onClick={() => onToggle(value)}
              >
                ×
              </button>
            )}
          </span>
        );
      })}

      {disabled ? null : (
        <button
          type="button"
          className="kgb-add-link"
          aria-label={`Add ${label}`}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          {values.length ? "+ Add" : `+ Add ${label.toLowerCase()}`}
        </button>
      )}

      {open ? (
        <InlinePicker
          anchorRef={cellRef}
          title={label}
          options={catalogue}
          selected={values}
          onPick={pick}
          onClose={close}
          creatable={creatable}
        />
      ) : null}
    </div>
  );
}

export default memo(ChipsCell);
